import { ECONOMIC_IMPACT_TIMEOUT_MS } from '../constants';
import { HEALTH_CHECK_FRAGMENTS } from './fragments';

export const getHealthCheckId = (
  service: string,
  viewGroup: string,
  view: string
): string => {
  return `${service}:${viewGroup}:${view}`;
};

export interface HealthCheckResult {
  id: string;
  timestamp: string;
  service: string;
  viewGroup: string;
  view: string;
  status: 'success' | 'failure';
  responseTime: number;
  error?: {
    message: string;
    details: unknown;
  };
  data: Record<string, unknown>;
  isManualCheck: boolean;
  userEmail?: string;
  /** `false` for a playground run of an edited query — executed by the runner but never stored. */
  recorded?: boolean;
}

type HealthCheckIdentity = {
  service: string;
  viewGroup: string;
  view: string;
  /** Optional link surfaced in Slack alerts; `null` suppresses the link. */
  customUrl?: string | null;
  /**
   * Root field on the public GraphQL API that this check exercises
   * (tools/scripts/spectaql/obscured-introspection.json is the source of
   * truth). Drives the per-query API-docs link in the health-check UI and
   * the docs↔health-check alignment test.
   */
  publicOp?: string;
  /** The public op is deprecated in the API docs; `replacement` names its successor. */
  deprecated?: boolean;
  replacement?: string;
  /**
   * Explicit docs link for checks whose documentation lives outside the
   * SpectaQL GraphQL docs (e.g. the Data API's OpenAPI pages). When absent,
   * the UI derives the link from `publicOp`.
   */
  docsUrl?: string;
  /**
   * One-line blast-radius note surfaced in Slack alerts — what a user loses
   * while this check is red. Page checks derive it from their viewGroup/view;
   * set it by hand for upstream/shared/external checks.
   */
  impact?: string;
  /** Which monitor-registry group the check renders under. */
  registryGroup?: RegistryGroupId;
  /**
   * Matrix placement for page checks: the coverage matrix cell is
   * (domain row, entity column). Checks without `entity` render as registry
   * rows. Checks with `entity` AND `domain` are page variants: that row's tab
   * and a matrix cell. Checks with `entity` and NO `domain` are dimension
   * probes of a public-only op: a tab on the row only (see
   * health-check-alignment.spec.ts for the rules).
   */
  domain?: MatrixDomainId;
  entity?: MatrixEntityId;
};

export type RegistryGroupId =
  | 'auth'
  | 'chart-data'
  | 'filtering'
  | 'discovery'
  | 'company-mapper'
  | 'contact-api'
  | 'docs'
  | 'data-builder'
  | 'reports'
  | 'modelling'
  | 'upstream'
  | 'deprecated';

/** Registry render order — deprecated is deliberately last. */
export const REGISTRY_GROUPS: ReadonlyArray<{
  id: RegistryGroupId;
  label: string;
}> = [
  { id: 'auth', label: 'Auth' },
  { id: 'chart-data', label: 'GraphQL · Chart Data' },
  { id: 'filtering', label: 'GraphQL · Filtering' },
  { id: 'discovery', label: 'GraphQL · Discovery' },
  { id: 'company-mapper', label: 'Company Mapper' },
  { id: 'contact-api', label: 'Contact API' },
  { id: 'docs', label: 'Documentation' },
  { id: 'data-builder', label: 'Data Builder' },
  { id: 'reports', label: 'Reports' },
  { id: 'modelling', label: 'Modelling' },
  { id: 'upstream', label: 'Upstream' },
  { id: 'deprecated', label: 'GraphQL · Deprecated' },
];

export type MatrixEntityId =
  | 'company'
  | 'industry'
  | 'geography'
  | 'role'
  | 'skill'
  | 'activity';

export const MATRIX_ENTITIES: ReadonlyArray<{
  id: MatrixEntityId;
  label: string;
}> = [
  { id: 'company', label: 'Company' },
  { id: 'industry', label: 'Industry' },
  { id: 'geography', label: 'Geography' },
  { id: 'role', label: 'Role' },
  { id: 'skill', label: 'Skill' },
  { id: 'activity', label: 'Activity' },
];

export type MatrixDomainId =
  | 'composition'
  | 'postings'
  | 'transitions'
  | 'pay'
  | 'sentiment'
  | 'screener'
  | 'market-conditions'
  | 'diversity';

/**
 * Coverage-matrix rows — one per page. Cells without a check render dashed.
 * Labels follow the dashboard's page names (Overview, Demand, Compensation);
 * ids keep the original domain names, as the routes still do.
 */
export const MATRIX_DOMAINS: ReadonlyArray<{
  id: MatrixDomainId;
  label: string;
}> = [
  { id: 'composition', label: 'overview' },
  { id: 'postings', label: 'demand' },
  { id: 'transitions', label: 'transitions' },
  { id: 'pay', label: 'compensation' },
  { id: 'sentiment', label: 'sentiment' },
  { id: 'screener', label: 'screener' },
  { id: 'market-conditions', label: 'market-conditions' },
  { id: 'diversity', label: 'diversity' },
];

/**
 * The SpectaQL docs build publishes to `/docs/` on the dashboard domain
 * (see firebase.json rewrites + tools/scripts/spectaql/combined.ts) with
 * one `#query-<op>` anchor per query — the same anchor ids
 * tools/scripts/spectaql/deprecations.json references.
 */
export const getApiDocsUrl = (publicOp: string): string =>
  `/docs/#query-${publicOp}`;

/** GraphQL-backed check — executed via `commonService.queryGoGql`. */
export type GraphQLHealthCheckConfig = HealthCheckIdentity & {
  query: (typeof HEALTH_CHECK_FRAGMENTS)[keyof typeof HEALTH_CHECK_FRAGMENTS];
  variables: Record<string, unknown>;
  /**
   * Aborts the request after this long and records a timeout failure. Unset
   * means no bound beyond the gateway's own. Sweeps run checks one at a time,
   * so set this on a check known to hang so it can't stall the checks behind it.
   */
  timeoutMs?: number;
  request?: never;
};

/**
 * REST-backed check — the dash-meta runner GETs `url` and treats any 2xx
 * as success. Use this for upstream dependencies (mappers, company-mapper)
 * that expose `/ping`-style readiness probes.
 *
 * `url` may include `{ENV_VAR}` placeholders that the dash-meta runner
 * resolves at request time (e.g. `{COMPANY_MAPPER_URL}/ping`), so this
 * config stays usable across environments without leaking env values
 * into the shared lib consumed by the dashboard.
 *
 * `useAdminAuth` sends the same admin session the GraphQL checks use (as
 * both Bearer token and kratos cookie, matching the pattern proven in
 * company.resolver) — for endpoints behind the platform's auth (e.g. the
 * go-api deliverables proxy, company-mapper /search) that have no
 * unauthenticated probe.
 *
 * A `body` makes the probe a POST with a JSON payload — use a minimal,
 * side-effect-free request that exercises the endpoint's real code path
 * (e.g. an actual company search) rather than just its router.
 */
export type RestHealthCheckConfig = HealthCheckIdentity & {
  request: {
    url: string;
    useAdminAuth?: boolean;
    body?: Record<string, unknown>;
    /**
     * Overrides the runner's default 10s abort timeout — for upstreams whose
     * healthy response time approaches it (e.g. the MRIO calculation).
     */
    timeoutMs?: number;
    /**
     * Extra request headers. Values may use the same `{ENV_VAR}` placeholders
     * as `url` (e.g. an `x-api-key`), resolved by the dash-meta runner so
     * secrets never live in this shared lib.
     */
    headers?: Record<string, string>;
    /**
     * Non-JSON POST payload sent verbatim (e.g. the company-mapper's CSV
     * mapping input). Declare the matching Content-Type in `headers`.
     * Mutually exclusive with `body`.
     */
    bodyRaw?: string;
    /**
     * Bespoke multi-step probes the dash-meta runner implements in code.
     * 'resume-parser' uploads a fixture resume once per process (cached)
     * and re-invokes the parser model on that existing upload each sweep.
     * 'economic-impact-roles' runs the solve probe's calculation, then posts
     * `body` to /roles with the `result_id` the solve handed back added.
     */
    flow?: 'resume-parser' | 'economic-impact-roles';
  };
  /**
   * Names the probe when several checks hit one endpoint (same service and
   * path), e.g. the county and city solves of economic-impact /invocations.
   * The board shows such an endpoint as one row with a tab per probe, so
   * every check of it needs one (health-check-alignment.spec.ts).
   */
  tab?: string;
  query?: never;
  variables?: never;
};

export type HealthCheckConfig =
  | GraphQLHealthCheckConfig
  | RestHealthCheckConfig;

export const isRestHealthCheck = (
  check: HealthCheckConfig
): check is RestHealthCheckConfig =>
  'request' in check && check.request != null;

/**
 * A REST check whose JSON body the playground can edit and run. GET probes
 * have nothing to edit, and raw-body probes send no JSON body. A scripted
 * flow's `body` is what its last call sends (economic-impact /roles, after
 * its solve); the resume-parser flow has none.
 * Shared by the board (whether to render the editor) and dash-meta (whether
 * to accept the run), so the two can never disagree.
 */
export const isPlaygroundEditableRestCheck = (
  check: HealthCheckConfig
): check is RestHealthCheckConfig =>
  isRestHealthCheck(check) &&
  check.request.body !== undefined &&
  check.request.bodyRaw === undefined;

/**
 * Builds valid `composition_v3` query variables for health checks. The exact
 * filter/taxonomy semantics don't matter here — the check only verifies the
 * endpoint returns composition data — so corrections/taxonomies/breakdown
 * levels use safe defaults.
 */
const compositionV3HealthCheckVariables = (
  entities: ReadonlyArray<{ EntID: number | string; EntType: string }>,
  dates: { start_date: string; end_date: string }
): Record<string, unknown> => ({
  entities,
  filters: { start_date: dates.start_date, end_date: dates.end_date },
  corrections: {
    with_weight: true,
    with_sample_weight: true,
    with_contingent_workers: false,
  },
  role_taxonomy: 0,
  skill_taxonomy: 0,
  breakdown_options: {
    geo_breakdown_level: 'REGION',
    rics_breakdown_level: 'RICS_K10',
    role_breakdown_level: 'ROLE_LVL_1',
    skill_breakdown_level: 'SKILL_LVL_1',
  },
});

/**
 * A representative `TalentDiscoveryReqV3` search (mixed text + attribute
 * filters). Shared by the talent-discovery page check and the public-API
 * checks for `talent_discovery_search_v3` / `talent_discovery_name_recommendations_v3`,
 * which take the same request type.
 */
const talentDiscoveryV3HealthCheckVariables = (): Record<string, unknown> => ({
  query: {
    filters: {
      expr: {
        user_and: {
          children: [
            {
              user_value: {
                child: {
                  txt: {
                    user_attribute: {
                      kind: 'DESC',
                      text: 'engineer',
                    },
                  },
                },
              },
            },
            {
              user_value: {
                child: {
                  txt: {
                    user_attribute: {
                      kind: 'DESC',
                      text: 'software',
                    },
                  },
                },
              },
            },
            {
              user_value: {
                child: {
                  val: {
                    user_attribute: {
                      id: 1,
                      kind: 'SKILL_LVL_1',
                    },
                  },
                },
              },
            },
            {
              user_value: {
                child: {
                  val: {
                    user_attribute: {
                      id: 8,
                      kind: 'SKILL_LVL_1',
                    },
                  },
                },
              },
            },
            {
              user_value: {
                child: {
                  val: {
                    user_attribute: {
                      id: 1,
                      kind: 'GENDER',
                    },
                  },
                },
              },
            },
            {
              user_value: {
                child: {
                  val: {
                    user_attribute: {
                      id: 2,
                      kind: 'ETHNICITY',
                    },
                  },
                },
              },
            },
          ],
        },
      },
      roles_tax: 'default',
      skills_tax: 0,
    },
    pagination: {
      page_num: 1,
      page_size: 5000,
    },
  },
});

/**
 * Builds a representative `postings_v3` request for one entity — the same
 * shape the v3 postings plot pages send (verified against the Bruno
 * `get-postings-v3` request).
 */
const postingsV3HealthCheckVariables = (entity: {
  EntID: number;
  EntType: string;
}): Record<string, unknown> => ({
  req: {
    entities: [entity],
    filters: {
      start_date: '2024-03-04',
      end_date: '2025-03-03',
      provider: [0],
    },
    options: { mode: 'NORMAL', roles_counts_level: 'ROLE_LVL_2' },
    role_taxonomy: 0,
    skill_taxonomy: 0,
  },
});

/**
 * A minimal layoffs deliverable (WARN notices for the EuroStoxx 50 set) —
 * the smallest spec /estimate and /verify accept, mirroring the Bruno
 * data-builder requests. Estimation and verification are read-only.
 */
const dataBuilderDeliverableSpec: Record<string, unknown> = {
  client: null,
  name: 'Data Builder',
  deliverables: [
    {
      type: 'layoffs',
      name: 'Layoffs',
      models: {
        timescaling_version: 3.2,
        job_taxonomy_version: 3,
        job_taxonomy_id: 0,
        skill_taxonomy_version: 3,
        skill_taxonomy_id: 0,
        activity_taxonomy_version: 3,
        activity_taxonomy_id: 0,
        contingent_worker_version: 1,
        salary_version: 2.1,
        weight_table: 2.1,
      },
      delivery: { type: 'download', format: 'csv' },
      unit_of_analysis: {
        type: 'company',
        value: { type: 'company_sets', company_sets: ['eurostoxx_50'] },
      },
      filters: null,
      files: [
        {
          type: 'individual_layoffs',
          columns: {
            preset: [
              'rcid',
              'company',
              'ultimate_parent_rcid',
              'ultimate_parent_company_name',
              'state',
              'metro_area',
              'city',
              'notice_date',
              'layoff_date',
              'num_employees',
            ],
            custom: null,
          },
        },
      ],
      settings: { source: 'warn' },
    },
  ],
  settings: { lag_data: 0 },
};

// Where the upstream REST contracts are documented. The model APIs serve no
// docs of their own, so these point at the repos that define them.
const ECONOMIC_IMPACT_API_DOCS =
  'https://github.com/opencareer/economic-impact/blob/main/economic-impact-api/API_SCHEMA.md';
const COMPANY_MAPPER_API_DOCS =
  'https://github.com/opencareer/company_mapper/blob/main/api.yaml';
const SKILL_MAPPER_API_DOCS =
  'https://github.com/opencareer/taxonomy_v3/blob/main/skill_mapping_api/README.md';
const TAXONOMY_API_DOCS =
  'https://github.com/opencareer/taxonomy_v3/blob/main/taxonomy-api/README.md';
// No README documents it; the Flask handler is the contract.
const LOCATION_MAPPER_API_DOCS =
  'https://github.com/opencareer/location-mapping-v4/blob/main/src/api/flask_wrapper.py';
// The backend's own Swagger page can't load through the go-api proxy (it
// fetches /openapi.json from the host root), so link the spec it serves.
const DATA_BUILDER_API_DOCS =
  'https://dashboard.prd.cloud.reveliolabs.com/proxy/deliverables/openapi.json';

/**
 * The economic-impact solve the probes send: a small single-industry
 * calculation (BEA code 1111A0) in Manhattan, side-effect-free. The invoke
 * probe posts it as is; the roles flow (dash-meta) posts it first to mint the
 * `result_id` that /roles needs. The report block mirrors the page's own
 * (useImpact.ts REPORT_OPTIONS) so a probe fails if the API stops accepting
 * what the page sends, rather than passing on options no one uses.
 */
export const ECONOMIC_IMPACT_PROBE_SOLVE = {
  primary_fips: '36061',
  fips: '36061',
  amount_dollars: 1000000,
  industry_code: '1111A0',
  report: {
    min_pct_of_investment: 0.001,
    include_brackets: true,
  },
};

export const HEALTH_CHECKS: HealthCheckConfig[] = [
  // Shared checks used across pages
  {
    service: 'users',
    query: HEALTH_CHECK_FRAGMENTS.Auth,
    impact: 'user/entitlement lookups on every page load',
    variables: {
      ids: ['test-user-id'],
    },
    viewGroup: 'shared',
    view: 'auth',
    registryGroup: 'auth',
    customUrl: null,
  },
  // The filter pickers load their trees through the taxonomy op family now
  // (the taxonomyTree migrations), so the taxonomy checks above are the
  // filtering surface. `selectionList` is still probed too — split by caller
  // further down, since parts of it remain live dashboard dependencies.
  // --- Page checks ---
  // One group per entity dimension, one view per dashboard page, and each
  // check runs the ops the page template actually queries — a red check
  // here means that page is broken for users. Services are named after the
  // exact GraphQL op. Pay pages probe both their ops: pay_v3 plus the
  // default-provider postings series their Time to Fill charts draw.
  // Company pages
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: '1116644', EntType: 'COMPANY' },
        { EntID: '919809', EntType: 'COMPANY' },
        { EntID: '1005273', EntType: 'COMPANY' },
        { EntID: '1010813', EntType: 'COMPANY' },
        { EntID: '256277', EntType: 'COMPANY' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'company',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'company',
  },
  {
    service: 'transitions_v3',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV3,
    publicOp: 'transitions_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        breakdown_options: {
          role_breakdown_level: 'ROLE_LVL_2',
          geo_breakdown_level: 'COUNTRY',
          rics_breakdown_level: 'RICS_K50',
          with_contingent_workers: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'company',
    view: 'transitions',
    registryGroup: 'chart-data',
    domain: 'transitions',
    entity: 'company',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 1116644,
      EntType: 'COMPANY',
    }),
    viewGroup: 'company',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'company',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'company',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'company',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 1116644,
      EntType: 'COMPANY',
    }),
    viewGroup: 'company',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'company',
  },
  {
    service: 'sentiment_v3',
    query: HEALTH_CHECK_FRAGMENTS.SentimentV3,
    publicOp: 'sentiment_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: {
          start_date: '2020-01',
          end_date: '2025-03',
          min_reviews: 1,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'company',
    view: 'sentiment',
    registryGroup: 'chart-data',
    domain: 'sentiment',
    entity: 'company',
  },
  // The diversity page is composition_v3 sliced by gender/ethnicity.
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: {
      entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
      filters: {
        start_date: '2024-03',
        end_date: '2025-03',
        gender: [1],
        ethnicity: [1, 2, 3],
      },
      corrections: {
        with_weight: true,
        with_sample_weight: true,
        with_contingent_workers: false,
      },
      role_taxonomy: 0,
      skill_taxonomy: 0,
      breakdown_options: {
        geo_breakdown_level: 'REGION',
        rics_breakdown_level: 'RICS_K10',
        role_breakdown_level: 'ROLE_LVL_1',
        skill_breakdown_level: 'SKILL_LVL_1',
      },
    },
    viewGroup: 'company',
    view: 'diversity',
    registryGroup: 'chart-data',
    domain: 'diversity',
    entity: 'company',
  },
  {
    service: 'screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    publicOp: 'screener',
    variables: {
      filters: {
        dim: 'COMPANY',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: { limit: 35, offset: 0 },
        primary_selector: {},
        segments: {
          attrition: [{ end_val: 0.25, filter: {}, start_val: 0 }],
          headcount: [
            { end_val: 10000, filter: { region: [14] }, start_val: 0 },
          ],
        },
      },
    },
    viewGroup: 'company',
    view: 'discovery',
    customUrl: '/company/screener',
    registryGroup: 'discovery',
    domain: 'screener',
    entity: 'company',
  },
  // Industry pages
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [{ EntID: 31, EntType: 'RICS_K50' }],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'industry',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'industry',
  },
  {
    service: 'transitions_v3',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV3,
    publicOp: 'transitions_v3',
    variables: {
      req: {
        entities: [{ EntID: 31, EntType: 'RICS_K50' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        breakdown_options: {
          role_breakdown_level: 'ROLE_LVL_2',
          geo_breakdown_level: 'COUNTRY',
          rics_breakdown_level: 'RICS_K50',
          with_contingent_workers: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'industry',
    view: 'transitions',
    registryGroup: 'chart-data',
    domain: 'transitions',
    entity: 'industry',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 31,
      EntType: 'RICS_K50',
    }),
    viewGroup: 'industry',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'industry',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 31, EntType: 'RICS_K50' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'industry',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'industry',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 31,
      EntType: 'RICS_K50',
    }),
    viewGroup: 'industry',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'industry',
  },
  {
    service: 'sentiment_v3',
    query: HEALTH_CHECK_FRAGMENTS.SentimentV3,
    publicOp: 'sentiment_v3',
    variables: {
      req: {
        entities: [{ EntID: 31, EntType: 'RICS_K50' }],
        filters: {
          start_date: '2020-01',
          end_date: '2025-03',
          min_reviews: 1,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'industry',
    view: 'sentiment',
    registryGroup: 'chart-data',
    domain: 'sentiment',
    entity: 'industry',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 31,
      EntType: 'RICS_K50',
    }),
    viewGroup: 'industry',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'industry',
  },
  // Geography pages
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 2, EntType: 'MSA' },
        { EntID: 1, EntType: 'MSA' },
        { EntID: 3, EntType: 'MSA' },
        { EntID: 21, EntType: 'MSA' },
        { EntID: 5, EntType: 'MSA' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'geography',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'geography',
  },
  {
    service: 'transitions_v3',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV3,
    publicOp: 'transitions_v3',
    variables: {
      req: {
        entities: [{ EntID: 2, EntType: 'MSA' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        breakdown_options: {
          role_breakdown_level: 'ROLE_LVL_2',
          geo_breakdown_level: 'COUNTRY',
          rics_breakdown_level: 'RICS_K50',
          with_contingent_workers: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'geography',
    view: 'transitions',
    registryGroup: 'chart-data',
    domain: 'transitions',
    entity: 'geography',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({ EntID: 2, EntType: 'MSA' }),
    viewGroup: 'geography',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'geography',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 2, EntType: 'MSA' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'geography',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'geography',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({ EntID: 2, EntType: 'MSA' }),
    viewGroup: 'geography',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'geography',
  },
  {
    service: 'sentiment_v3',
    query: HEALTH_CHECK_FRAGMENTS.SentimentV3,
    publicOp: 'sentiment_v3',
    variables: {
      req: {
        entities: [{ EntID: 2, EntType: 'MSA' }],
        filters: {
          start_date: '2020-01',
          end_date: '2025-03',
          min_reviews: 1,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'geography',
    view: 'sentiment',
    registryGroup: 'chart-data',
    domain: 'sentiment',
    entity: 'geography',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({ EntID: 2, EntType: 'MSA' }),
    viewGroup: 'geography',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'geography',
  },
  {
    service: 'screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    publicOp: 'screener',
    variables: {
      filters: {
        dim: 'GEOGRAPHY',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: { limit: 35, offset: 0 },
        primary_selector: {},
        segments: {
          attrition: [{ end_val: 0.25, filter: {}, start_val: 0 }],
          growth_yoy: [
            { end_val: 0.25, filter: { gender: [1] }, start_val: 0 },
          ],
        },
      },
    },
    viewGroup: 'geography',
    view: 'discovery',
    customUrl: '/geography/screener',
    registryGroup: 'discovery',
    domain: 'screener',
    entity: 'geography',
  },
  // Role pages
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 107, EntType: 'ROLE_LVL_3' },
        { EntID: 117, EntType: 'ROLE_LVL_3' },
        { EntID: 140, EntType: 'ROLE_LVL_3' },
        { EntID: 17, EntType: 'ROLE_LVL_3' },
        { EntID: 52, EntType: 'ROLE_LVL_3' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    viewGroup: 'role',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'role',
  },
  {
    service: 'transitions_v3',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV3,
    publicOp: 'transitions_v3',
    variables: {
      req: {
        entities: [{ EntID: 107, EntType: 'ROLE_LVL_3' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        breakdown_options: {
          role_breakdown_level: 'ROLE_LVL_2',
          geo_breakdown_level: 'COUNTRY',
          rics_breakdown_level: 'RICS_K50',
          with_contingent_workers: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'role',
    view: 'transitions',
    registryGroup: 'chart-data',
    domain: 'transitions',
    entity: 'role',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 107,
      EntType: 'ROLE_LVL_3',
    }),
    viewGroup: 'role',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'role',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 107, EntType: 'ROLE_LVL_3' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'role',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'role',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 107,
      EntType: 'ROLE_LVL_3',
    }),
    viewGroup: 'role',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'role',
  },
  {
    service: 'sentiment_v3',
    query: HEALTH_CHECK_FRAGMENTS.SentimentV3,
    publicOp: 'sentiment_v3',
    variables: {
      req: {
        entities: [{ EntID: 107, EntType: 'ROLE_LVL_3' }],
        filters: {
          start_date: '2020-01',
          end_date: '2025-03',
          min_reviews: 1,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'role',
    view: 'sentiment',
    registryGroup: 'chart-data',
    domain: 'sentiment',
    entity: 'role',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 107,
      EntType: 'ROLE_LVL_3',
    }),
    viewGroup: 'role',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'role',
  },
  {
    service: 'screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    publicOp: 'screener',
    variables: {
      filters: {
        dim: 'ROLE',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: { limit: 35, offset: 0 },
        primary_selector: {},
        segments: {
          tenure: [{ end_val: 2, filter: {}, start_val: 0 }],
        },
      },
    },
    viewGroup: 'role',
    view: 'discovery',
    customUrl: '/role/screener',
    registryGroup: 'discovery',
    domain: 'screener',
    entity: 'role',
  },
  // Skill pages. Skill has no transitions, sentiment, screener or diversity
  // page. Entities are the default skill selections (GENERATED_DEFAULT_CUSTOM_SKILL_DEFAULT): picker
  // level skill_0 = K150 = SKILL_LVL_3.
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 28, EntType: 'SKILL_LVL_3' },
        { EntID: 38, EntType: 'SKILL_LVL_3' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    // Two of the five defaults: on stage each skill costs ~14s, so three or
    // more exceed the gateway's 30s limit (backend question open). The
    // timeout stops a slow run sitting through the retry (~64s).
    timeoutMs: 35_000,
    viewGroup: 'skill',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'skill',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 103,
      EntType: 'SKILL_LVL_3',
    }),
    viewGroup: 'skill',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'skill',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 103, EntType: 'SKILL_LVL_3' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'skill',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'skill',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 103,
      EntType: 'SKILL_LVL_3',
    }),
    viewGroup: 'skill',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'skill',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 103,
      EntType: 'SKILL_LVL_3',
    }),
    viewGroup: 'skill',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'skill',
  },
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3PageData,
    publicOp: 'composition_v3',
    variables: {
      req: compositionV3HealthCheckVariables(
        [{ EntID: 103, EntType: 'SKILL_LVL_3' }],
        { start_date: '2024-03', end_date: '2025-03' }
      ),
    },
    viewGroup: 'skill',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'skill',
  },
  // Activity pages: the same page set as Skill (no transitions, sentiment,
  // screener or diversity page), released without a flag. Entities are the
  // default activity selections (GENERATED_DEFAULT_CUSTOM_ACTIVITY_DEFAULT) at
  // picker level activity_k150 = ACTIVITY_LVL_3.
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [
        { EntID: 13, EntType: 'ACTIVITY_LVL_3' },
        { EntID: 37, EntType: 'ACTIVITY_LVL_3' },
      ],
      { start_date: '2008-01', end_date: '2024-04' }
    ),
    // Two of the five defaults, for the same per-entity cost as the skill
    // overview above (~8s per activity on stage).
    timeoutMs: 35_000,
    viewGroup: 'activity',
    view: 'compositions',
    registryGroup: 'chart-data',
    domain: 'composition',
    entity: 'activity',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 13,
      EntType: 'ACTIVITY_LVL_3',
    }),
    viewGroup: 'activity',
    view: 'postings',
    registryGroup: 'chart-data',
    domain: 'postings',
    entity: 'activity',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 13, EntType: 'ACTIVITY_LVL_3' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'activity',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'activity',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 13,
      EntType: 'ACTIVITY_LVL_3',
    }),
    viewGroup: 'activity',
    view: 'pay',
    registryGroup: 'chart-data',
    domain: 'pay',
    entity: 'activity',
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 13,
      EntType: 'ACTIVITY_LVL_3',
    }),
    viewGroup: 'activity',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'activity',
  },
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3PageData,
    publicOp: 'composition_v3',
    variables: {
      req: compositionV3HealthCheckVariables(
        [{ EntID: 13, EntType: 'ACTIVITY_LVL_3' }],
        { start_date: '2024-03', end_date: '2025-03' }
      ),
    },
    viewGroup: 'activity',
    view: 'market-conditions',
    registryGroup: 'chart-data',
    domain: 'market-conditions',
    entity: 'activity',
  },

  // Reports builder checks
  {
    service: 'reports_validation',
    query: HEALTH_CHECK_FRAGMENTS.ReportConfigHealthCheck,
    variables: {},
    viewGroup: 'deliverables',
    view: 'report-builder',
    registryGroup: 'reports',
    customUrl: 'report-builder/report_type',
  },
  // Reports view checks
  {
    service: 'userSearch',
    query: HEALTH_CHECK_FRAGMENTS.UserSearchHealthCheck,
    variables: {
      input: 'revelio',
    },
    viewGroup: 'deliverables',
    view: 'report-builder',
    registryGroup: 'reports',
    customUrl: '/reports',
  },
  // Talent Discovery V3 checks

  // --- Public GraphQL API surface ---
  // One canonical check per NON-DEPRECATED query the public API docs expose
  // (tools/scripts/spectaql/obscured-introspection.json). Deprecating an op
  // retires its probe: the docs keep publishing it for consumers who have not
  // migrated, but the sweep measures what we ask people to use — grading a
  // superseded op alongside the live surface only cost us triage time.
  // The exception is an op WE have not migrated off either, which is what the
  // `deprecated`/`replacement` fields are for: tag such a check and it sweeps
  // prod-only and renders as before. `selectionList` is the only one today,
  // and it carries `impact` notes naming the callers that keep it alive.
  // The alignment test
  // (health-check-alignment.spec.ts) fails the build when the docs and this
  // list drift apart. Two ops are deliberately absent:
  // `posting_discovery_search_by_id` and `talent_discovery_search_by_id_v3`
  // both require an id minted by an immediately-preceding search, so a static
  // probe would rot — their search siblings cover the shared resolver path.
  {
    service: 'entityV3',
    query: HEALTH_CHECK_FRAGMENTS.EntityV3,
    publicOp: 'entityV3',
    variables: {
      req: { entID: 1116644, entType: 'COMPANY', numCompetitors: 7 },
    },
    viewGroup: 'graphql-api',
    view: 'entity',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'composition_v3',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV3,
    publicOp: 'composition_v3',
    variables: compositionV3HealthCheckVariables(
      [{ EntID: 1116644, EntType: 'COMPANY' }],
      { start_date: '2024-03', end_date: '2025-03' }
    ),
    viewGroup: 'graphql-api',
    view: 'composition',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'composition_v4',
    query: HEALTH_CHECK_FRAGMENTS.CompositionV4,
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: { start_date: '2024-03', end_date: '2025-03' },
        corrections: {
          with_weight: true,
          with_sample_weight: true,
          workforce_type: 'FULL_TIME',
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
        breakdowns: [
          { dimension: 'ROLE', level: 'ROLE_LVL_1', top_n: 7 },
          { dimension: 'GEOGRAPHY', level: 'REGION', top_n: 7 },
          { dimension: 'SENIORITY', top_n: 7 },
        ],
      },
    },
    viewGroup: 'graphql-api',
    view: 'composition',
    registryGroup: 'chart-data',
    customUrl: null,
    impact: 'composition charts on every page with the composition-v4 flag on',
  },
  {
    service: 'pay_v3',
    query: HEALTH_CHECK_FRAGMENTS.PayV3,
    publicOp: 'pay_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        corrections: {
          with_sample_weight: true,
          with_weight: true,
          with_contingent_workers: false,
        },
        options: {
          required_top_companies_length: 10,
          required_top_geos_length: 10,
          required_top_roles_length: 10,
          geo_breakdown_state_over_msa: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'graphql-api',
    view: 'pay',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'postings_v3',
    query: HEALTH_CHECK_FRAGMENTS.PostingsV3,
    publicOp: 'postings_v3',
    variables: postingsV3HealthCheckVariables({
      EntID: 1116644,
      EntType: 'COMPANY',
    }),
    viewGroup: 'graphql-api',
    view: 'postings',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'transitions_v3',
    query: HEALTH_CHECK_FRAGMENTS.TransitionsV3,
    publicOp: 'transitions_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: { start_date: '2023-01', end_date: '2025-03' },
        breakdown_options: {
          role_breakdown_level: 'ROLE_LVL_2',
          geo_breakdown_level: 'COUNTRY',
          rics_breakdown_level: 'RICS_K50',
          with_contingent_workers: false,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'graphql-api',
    view: 'transitions',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'sentiment_v3',
    query: HEALTH_CHECK_FRAGMENTS.SentimentV3,
    publicOp: 'sentiment_v3',
    variables: {
      req: {
        entities: [{ EntID: 1116644, EntType: 'COMPANY' }],
        filters: {
          start_date: '2020-01',
          end_date: '2025-03',
          min_reviews: 1,
        },
        role_taxonomy: 0,
        skill_taxonomy: 0,
      },
    },
    viewGroup: 'graphql-api',
    view: 'sentiment',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'screener',
    query: HEALTH_CHECK_FRAGMENTS.Screener,
    publicOp: 'screener',
    variables: {
      filters: {
        dim: 'COMPANY',
        entity_info_selectors: {},
        contingent_workers: false,
        pagination: { limit: 35, offset: 0 },
        primary_selector: {},
        segments: {
          attrition: [{ end_val: 0.25, filter: {}, start_val: 0 }],
        },
      },
    },
    viewGroup: 'graphql-api',
    view: 'screener',
    registryGroup: 'discovery',
    customUrl: null,
  },
  {
    service: 'mapCoords',
    query: HEALTH_CHECK_FRAGMENTS.GeographySummaryMapData,
    publicOp: 'mapCoords',
    variables: {
      query: {
        country: [],
        msa: [{ granularity: 'MSA', id: 2 }],
        region: [],
      },
    },
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'chart-data',
    customUrl: null,
  },
  {
    service: 'talent_discovery_search_v3',
    query: HEALTH_CHECK_FRAGMENTS.TalentDiscoveryV3,
    publicOp: 'talent_discovery_search_v3',
    variables: talentDiscoveryV3HealthCheckVariables(),
    viewGroup: 'graphql-api',
    view: 'talent-discovery',
    registryGroup: 'discovery',
    customUrl: null,
  },
  {
    service: 'talent_discovery_name_recommendations_v3',
    query: HEALTH_CHECK_FRAGMENTS.TalentDiscoveryNameRecommendationsV3,
    publicOp: 'talent_discovery_name_recommendations_v3',
    variables: talentDiscoveryV3HealthCheckVariables(),
    viewGroup: 'graphql-api',
    view: 'talent-discovery',
    registryGroup: 'discovery',
    customUrl: null,
  },
  {
    service: 'talent_discovery_search_by_id_v3',
    query: HEALTH_CHECK_FRAGMENTS.TalentDiscoveryByIdV3,
    publicOp: 'talent_discovery_search_by_id_v3',
    // A durable person id, the same way the entity probes pin a company. The
    // op takes ids minted by a search, but they persist, so this needs no
    // preceding request — it goes red if the person leaves the index, which
    // is itself worth knowing.
    variables: {
      query: {
        original_users_ids: [143265487],
        filters: { roles_tax: 'default', skills_tax: 0 },
      },
    },
    viewGroup: 'graphql-api',
    view: 'talent-discovery-by-id',
    registryGroup: 'discovery',
    customUrl: null,
  },
  {
    service: 'posting_discovery_search',
    query: HEALTH_CHECK_FRAGMENTS.PostingDiscoverySearch,
    publicOp: 'posting_discovery_search',
    variables: {
      query: {
        filters: {
          expr: {
            posting_value: {
              child: {
                txt: { attribute: { kind: 'JOBTITLE', text: 'engineer' } },
              },
            },
          },
          roles_tax: 0,
          skills_tax: 0,
        },
        options: { include_descriptions: false },
        pagination: { page_num: 1, page_size: 5 },
      },
    },
    viewGroup: 'graphql-api',
    view: 'posting-discovery',
    registryGroup: 'discovery',
    customUrl: null,
  },
  {
    service: 'taxonomy',
    query: HEALTH_CHECK_FRAGMENTS.Taxonomy,
    publicOp: 'taxonomy',
    variables: {
      req: { taxonomy_type: 'ROLE', taxonomy_id: 31, levels: [1] },
    },
    viewGroup: 'graphql-api',
    view: 'taxonomy',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'taxonomyList',
    query: HEALTH_CHECK_FRAGMENTS.TaxonomyList,
    publicOp: 'taxonomyList',
    variables: { req: { taxonomy_type: 'ROLE' } },
    viewGroup: 'graphql-api',
    view: 'taxonomy',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'taxonomyVersions',
    query: HEALTH_CHECK_FRAGMENTS.TaxonomyVersions,
    publicOp: 'taxonomyVersions',
    variables: { req: { taxonomy_type: 'ROLE' } },
    viewGroup: 'graphql-api',
    view: 'taxonomy',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'taxonomyLatestVersion',
    query: HEALTH_CHECK_FRAGMENTS.TaxonomyLatestVersion,
    publicOp: 'taxonomyLatestVersion',
    variables: { req: { taxonomy_type: 'ROLE' } },
    viewGroup: 'graphql-api',
    view: 'taxonomy',
    registryGroup: 'filtering',
    customUrl: null,
  },
  // --- selectionList: split by caller ---
  // The docs deprecate `selectionList` in favour of `taxonomy`, but the
  // dashboard has NOT migrated off it. One probe per surviving caller, so the
  // board and the Slack alert name WHERE a failure lands rather than pointing
  // at a single opaque "selectionList" row. `impact` on each is the note that
  // carries that through to the alert.
  //
  // The first two are live dashboard dependencies with nothing deprecated
  // about how we use them, so they sit in `filtering` with the other
  // reference lists and sweep in every environment. They carry no `publicOp`:
  // the deprecated rows below are the public-API surface for this op, and
  // tagging these would force them `deprecated` too (the alignment spec
  // requires the tag to match the docs exactly).
  {
    service: 'selectionListDates',
    query: HEALTH_CHECK_FRAGMENTS.SelectionListDates,
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    impact: 'CustomDash date labels — week/month index lookup',
    customUrl: null,
  },
  {
    service: 'selectionListCurrency',
    query: HEALTH_CHECK_FRAGMENTS.SelectionListCurrency,
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    impact: 'currency preference editor — currency list and rates',
    customUrl: null,
  },
  // These two stay deprecated: they are the public-API surface for the op, so
  // the row keeps its docs link, its `→ taxonomy` badge and its prod-only
  // sweep. `impact` is what stops a reader concluding nobody uses them.
  {
    service: 'selectionList',
    query: HEALTH_CHECK_FRAGMENTS.SharedSelectionList,
    publicOp: 'selectionList',
    deprecated: true,
    replacement: 'taxonomy',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'taxonomy',
    registryGroup: 'deprecated',
    impact: "DataBuilder's V2 role and skill selection lists",
    customUrl: null,
  },
  {
    service: 'index',
    query: HEALTH_CHECK_FRAGMENTS.MonthWeekIndex,
    publicOp: 'index',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'cityInMsaInfo',
    query: HEALTH_CHECK_FRAGMENTS.CityInMsaInfo,
    publicOp: 'cityInMsaInfo',
    variables: { in: { id: '1' } },
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'schoolInfo',
    query: HEALTH_CHECK_FRAGMENTS.SchoolInfo,
    publicOp: 'schoolInfo',
    variables: { in: { name: 'stanford', page: 1 } },
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'ethnicity',
    query: HEALTH_CHECK_FRAGMENTS.Ethnicity,
    publicOp: 'ethnicity',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'gender',
    query: HEALTH_CHECK_FRAGMENTS.Gender,
    publicOp: 'gender',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'highestDegree',
    query: HEALTH_CHECK_FRAGMENTS.HighestDegree,
    publicOp: 'highestDegree',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'seniority',
    query: HEALTH_CHECK_FRAGMENTS.Seniority,
    publicOp: 'seniority',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'employmentRelationship',
    query: HEALTH_CHECK_FRAGMENTS.EmploymentRelationship,
    publicOp: 'employmentRelationship',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'remoteType',
    query: HEALTH_CHECK_FRAGMENTS.RemoteType,
    publicOp: 'remoteType',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },
  {
    service: 'workSchedule',
    query: HEALTH_CHECK_FRAGMENTS.WorkSchedule,
    publicOp: 'workSchedule',
    variables: {},
    viewGroup: 'graphql-api',
    view: 'reference',
    registryGroup: 'filtering',
    customUrl: null,
  },

  // The published SpectaQL docs themselves — every graphql-api check links
  // to an anchor on this page, so its liveness belongs in the same group.
  // The docs are one public prod surface regardless of which env probes it.
  {
    service: 'ApiDocs',
    viewGroup: 'graphql-api',
    view: 'docs',
    registryGroup: 'docs',
    customUrl: null,
    docsUrl: 'https://terminal.reveliolabs.com/docs/',
    impact: 'public GraphQL API documentation',
    request: {
      url: 'https://terminal.reveliolabs.com/docs/',
    },
  },

  // --- Dimension probes ---
  // Public-only ops (no dashboard page runs them) accept more entity kinds than
  // the company the canonical check sends, and external consumers call them
  // with those kinds. One probe per extra dimension the op's input type
  // accepts. `entity` without `domain` = a tab on the op's registry row, never
  // a coverage-matrix cell. Ids/dates mirror the page probes:
  // industry RICS_K50 31, geography MSA 2, role 107 (ROLE_K150 / role_k150).
  {
    service: 'entityV3',
    query: HEALTH_CHECK_FRAGMENTS.EntityV3,
    publicOp: 'entityV3',
    variables: {
      req: { entID: 107, entType: 'ROLE_K150', numCompetitors: 7 },
    },
    viewGroup: 'graphql-api',
    view: 'entity-role',
    registryGroup: 'chart-data',
    entity: 'role',
    customUrl: null,
  },

  // --- Data API (external REST, api-key auth) ---
  // The second externally available surface next to the GraphQL API:
  // https://data.api.prd.cloud.reveliolabs.com/docs/. The docs probe needs
  // no auth; the example enrichment request authenticates with the
  // DATA_API_KEY env var on dash-meta and burns one enrichment call per
  // sweep — keep the cadence in mind if more example checks are added.
  {
    service: 'DataApiDocs',
    viewGroup: 'data-api',
    view: 'docs',
    registryGroup: 'docs',
    impact: 'external Data API consumers',
    customUrl: null,
    docsUrl: 'https://data.api.prd.cloud.reveliolabs.com/docs/',
    request: {
      url: 'https://data.api.prd.cloud.reveliolabs.com/docs/',
    },
  },
  {
    service: 'ContactEnrichment',
    viewGroup: 'data-api',
    view: 'contact-enrichment',
    registryGroup: 'contact-api',
    impact: 'external Data API consumers',
    customUrl: null,
    docsUrl: 'https://data.api.prd.cloud.reveliolabs.com/docs/',
    request: {
      url: 'https://data.api.prd.cloud.reveliolabs.com/enrichment/contact/v1',
      headers: { 'x-api-key': '{DATA_API_KEY}' },
      body: {
        linkedin_url: 'https://www.linkedin.com/in/joshdanielson/',
      },
    },
  },

  // --- Upstream REST dependencies ---
  // These services back the AI-search flows. Each host below mirrors the
  // URL the dash-meta runtime actually hits — see
  //   apps/dash-meta/src/ai-search/utils/{skill,location,role}-mapper-api.ts
  // — so a green probe is evidence the dependency users hit is up (the
  // runtime URLs are hardcoded there, not env-driven). Each mapper gets two
  // checks: a `/ping` liveness probe, and an example inference request that
  // exercises the model path itself — a pod can answer /ping while its model
  // endpoint 5xxs. Company-mapper has no unauthenticated probe (`/ping`
  // 401s), so its coverage is the authenticated CompanySearch check at the
  // bottom of this list. Role inference runs on taxonomy-api's /infer — the
  // old role-mapping.gp0 host is decommissioned and no longer checked.
  {
    service: 'SkillMapper',
    viewGroup: 'upstream',
    view: 'skill-mapper',
    registryGroup: 'modelling',
    impact: 'AI-search skill filters',
    customUrl: null,
    docsUrl: `${SKILL_MAPPER_API_DOCS}#get-ping`,
    request: {
      url: 'https://skill-mapping.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  {
    service: 'SkillMapper',
    viewGroup: 'upstream',
    view: 'skill-mapper-invoke',
    registryGroup: 'modelling',
    impact: 'AI-search skill filters',
    customUrl: null,
    docsUrl: `${SKILL_MAPPER_API_DOCS}#post-invocations`,
    request: {
      url: 'https://skill-mapping.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/invocations',
      body: {
        skills: ['game development'],
        additional_fields: ['score', 'skill_k50'],
      },
    },
  },
  {
    service: 'LocationMapper',
    viewGroup: 'upstream',
    view: 'location-mapper',
    registryGroup: 'modelling',
    impact: 'AI-search location filters',
    customUrl: null,
    docsUrl: LOCATION_MAPPER_API_DOCS,
    request: {
      url: 'https://location-mapper.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  {
    service: 'LocationMapper',
    viewGroup: 'upstream',
    view: 'location-mapper-invoke',
    registryGroup: 'modelling',
    impact: 'AI-search location filters',
    customUrl: null,
    docsUrl: LOCATION_MAPPER_API_DOCS,
    request: {
      url: 'https://location-mapper.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/invocations',
      body: {
        locations: ['New York, NY'],
      },
    },
  },
  // Taxonomy API — serves role inference (see role-mapper-api.ts, which
  // despite its name calls taxonomy-api /infer) plus taxonomy normalization.
  {
    service: 'TaxonomyApi',
    viewGroup: 'upstream',
    view: 'taxonomy-api',
    registryGroup: 'modelling',
    impact: 'AI-search role inference',
    customUrl: null,
    docsUrl: `${TAXONOMY_API_DOCS}#get-ping`,
    request: {
      url: 'https://taxonomy-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
    },
  },
  {
    service: 'TaxonomyApi',
    viewGroup: 'upstream',
    view: 'taxonomy-api-infer',
    registryGroup: 'modelling',
    impact: 'AI-search role inference',
    customUrl: null,
    docsUrl: `${TAXONOMY_API_DOCS}#post-infer`,
    request: {
      url: 'https://taxonomy-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/infer',
      body: {
        job_info: [{ id: '0', title: 'software engineer' }],
        focus_on: ['title'],
        fields: ['role_k10', 'role_k150'],
        return_confidence_score: true,
      },
    },
  },
  // Economic-impact MRIO API — the dash-meta proxy forwards /ping,
  // /invocations and /roles to it (see economic-impact.controller.ts). Each
  // probe waits as long as the proxy does (ECONOMIC_IMPACT_TIMEOUT_MS), so it
  // fails only when the page would.
  {
    service: 'EconomicImpactApi',
    viewGroup: 'upstream',
    view: 'economic-impact',
    registryGroup: 'modelling',
    impact: 'economic-impact page',
    customUrl: null,
    docsUrl: `${ECONOMIC_IMPACT_API_DOCS}#health-check`,
    request: {
      url: 'https://economic-impact-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/ping',
      timeoutMs: ECONOMIC_IMPACT_TIMEOUT_MS.ping,
    },
  },
  {
    service: 'EconomicImpactApi',
    viewGroup: 'upstream',
    view: 'economic-impact-invoke',
    registryGroup: 'modelling',
    impact: 'economic-impact page',
    customUrl: null,
    docsUrl: `${ECONOMIC_IMPACT_API_DOCS}#invocation-endpoint`,
    tab: 'county',
    request: {
      url: 'https://economic-impact-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/invocations',
      body: ECONOMIC_IMPACT_PROBE_SOLVE,
      timeoutMs: ECONOMIC_IMPACT_TIMEOUT_MS.solve,
    },
  },
  // The role split of that solve, as the Employment tab asks for it right
  // after a solve. /roles only answers for a `result_id` the API minted in the
  // last hour, so the body leaves it out: the runner solves, then posts this
  // body with that solve's handle added (see health-check.service.ts
  // invokeEconomicImpactRoles). It is the Bruno request's body
  // (economic-impact/roles.bru). Waits for both calls, as the page does.
  {
    service: 'EconomicImpactApi',
    viewGroup: 'upstream',
    view: 'economic-impact-roles',
    registryGroup: 'modelling',
    impact: 'economic-impact page (Employment by occupation)',
    customUrl: null,
    docsUrl: `${ECONOMIC_IMPACT_API_DOCS}#post-roles`,
    request: {
      url: 'https://economic-impact-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/roles',
      flow: 'economic-impact-roles',
      body: {
        taxonomy_level: 'role_k10',
        top_n: 100,
        node_keys: [ECONOMIC_IMPACT_PROBE_SOLVE.primary_fips],
      },
      timeoutMs:
        ECONOMIC_IMPACT_TIMEOUT_MS.solve + ECONOMIC_IMPACT_TIMEOUT_MS.roles,
    },
  },
  // The same calculation placed in a city, as the page sends it under the
  // Cities geography preference: Houston (32773 in the city taxonomy) through
  // the location bridge, reported by city. Fails if the bridge artifacts are
  // missing, which the county probe above can't see.
  {
    service: 'EconomicImpactApi',
    viewGroup: 'upstream',
    view: 'economic-impact-invoke-city',
    registryGroup: 'modelling',
    impact: 'economic-impact page (cities)',
    customUrl: null,
    docsUrl: `${ECONOMIC_IMPACT_API_DOCS}#invocation-endpoint`,
    tab: 'city',
    request: {
      url: 'https://economic-impact-api.gp0.k8s.dc1.rlc.cloud.reveliolabs.com/invocations',
      body: {
        primary_fips: '36061',
        investments: [
          {
            location_taxonomy_id: 'revelio_city_core_v4_1',
            location_code: '32773',
            amount_dollars: 1000000,
            industry_code: '1111A0',
          },
        ],
        report: {
          min_pct_of_investment: 0.001,
          result_location_taxonomy_id: 'revelio_city_core_v4_1',
        },
      },
      timeoutMs: ECONOMIC_IMPACT_TIMEOUT_MS.solve,
    },
  },
  // Unleash — server-side feature flags. Same env pair UnleashService uses;
  // /client/features with the client token is the lightest authenticated call.
  // On an outage flags fall back to their code defaults, silently changing
  // behavior — worth an alert even though the app stays up.
  {
    service: 'Unleash',
    viewGroup: 'upstream',
    view: 'unleash',
    registryGroup: 'upstream',
    customUrl: null,
    impact: 'feature flags (silently fall back to code defaults)',
    request: {
      url: '{UNLEASH_URL}client/features',
      headers: { Authorization: '{UNLEASH_API_KEY}' },
    },
  },
  // LLM providers — /models and /key are free metadata endpoints that prove
  // both reachability and that our key is valid, without spending tokens.
  {
    service: 'OpenAi',
    viewGroup: 'upstream',
    view: 'openai',
    registryGroup: 'upstream',
    customUrl: null,
    impact: 'AI assistant + AI search',
    request: {
      url: 'https://api.openai.com/v1/models',
      headers: { Authorization: 'Bearer {OPENAI_API_KEY}' },
    },
  },
  {
    service: 'OpenRouter',
    viewGroup: 'upstream',
    view: 'openrouter',
    registryGroup: 'upstream',
    customUrl: null,
    impact: 'Build-a-dash AI generation',
    request: {
      url: 'https://openrouter.ai/api/v1/key',
      headers: { Authorization: 'Bearer {AI_DASH_GEN_API_KEY}' },
    },
  },
  // Reports host — serves generated report/deliverable downloads.
  {
    service: 'ReportsHost',
    viewGroup: 'upstream',
    view: 'reports-host',
    registryGroup: 'upstream',
    customUrl: null,
    impact: 'report + deliverable downloads',
    request: {
      url: '{REPORTS_URL}',
    },
  },
  // Ory Kratos — every login and session check flows through it; if it's
  // down, nobody can sign in. /health/alive is Ory's standard public probe.
  {
    service: 'Kratos',
    viewGroup: 'upstream',
    view: 'kratos',
    registryGroup: 'auth',
    impact: 'ALL sign-ins and sessions',
    customUrl: null,
    request: {
      url: '{ORY_KRATOS_URL}/health/alive',
    },
  },
  // OnlyOffice DocSpace backs deliverables document viewing/editing.
  {
    service: 'OnlyOffice',
    viewGroup: 'upstream',
    view: 'onlyoffice',
    registryGroup: 'upstream',
    impact: 'deliverables document viewing/editing',
    customUrl: null,
    request: {
      url: '{ONLYOFFICE_BASE_URL}/healthcheck',
    },
  },
  // The account-portal backend (user-mgmt app) — account settings, seats,
  // billing. Its root route is the lightest liveness signal it exposes.
  {
    service: 'AccountPortalApi',
    viewGroup: 'upstream',
    view: 'account-portal',
    registryGroup: 'upstream',
    impact: 'account portal (seats, billing, settings)',
    customUrl: null,
    request: {
      url: '{ACCOUNT_PORTAL_BACKEND_ROOT}/api',
    },
  },
  // The DataBuilder v2 backend, proxied through the go-api host. A separate
  // upstream from GraphQL — it can be down while every GraphQL check is
  // green. GET / is what DataBuilderRouteGuard fires on page load; the proxy
  // rejects anonymous requests, so reuse the admin auth the GraphQL checks
  // already hold.
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-api',
    registryGroup: 'data-builder',
    impact: 'Data Builder',
    customUrl: null,
    docsUrl: DATA_BUILDER_API_DOCS,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/',
      useAdminAuth: true,
    },
  },
  // The remaining side-effect-free Data Builder endpoints, with the same
  // example payloads the Bruno collection exercises. get-settings is
  // deliberately absent: it takes a concrete client_name, and no single
  // client exists across every environment. Mutating endpoints
  // (submit/create/delete/share) stay out of health checks.
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-status',
    registryGroup: 'data-builder',
    impact: 'Data Builder',
    customUrl: null,
    docsUrl: DATA_BUILDER_API_DOCS,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/status',
      useAdminAuth: true,
    },
  },
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-list-datasets',
    registryGroup: 'data-builder',
    impact: 'Data Builder',
    customUrl: null,
    docsUrl: DATA_BUILDER_API_DOCS,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/list-datasets',
      useAdminAuth: true,
    },
  },
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-estimate',
    registryGroup: 'data-builder',
    impact: 'Data Builder',
    customUrl: null,
    docsUrl: DATA_BUILDER_API_DOCS,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/estimate',
      useAdminAuth: true,
      body: dataBuilderDeliverableSpec,
    },
  },
  {
    service: 'DataBuilderApi',
    viewGroup: 'upstream',
    view: 'data-builder-verify',
    registryGroup: 'data-builder',
    impact: 'Data Builder',
    customUrl: null,
    docsUrl: DATA_BUILDER_API_DOCS,
    request: {
      url: '{GO_API_ROOT}/proxy/deliverables/verify',
      useAdminAuth: true,
      body: dataBuilderDeliverableSpec,
    },
  },
  // Functional company search — the same POST /search call the dashboard
  // fires for company validation/lookup (see useValidateCompanies.ts and
  // useCompanyRics.ts). Goes beyond the host /ping above: a green result
  // means the search index itself answered a real query.
  {
    service: 'CompanyMapper',
    viewGroup: 'upstream',
    view: 'company-search',
    registryGroup: 'company-mapper',
    impact: 'company search + validation everywhere',
    customUrl: null,
    docsUrl: COMPANY_MAPPER_API_DOCS,
    request: {
      url: '{COMPANY_MAPPER_URL}/search',
      useAdminAuth: true,
      body: {
        name: 'openai',
        employee_cnt_min: 1,
      },
    },
  },
  // The company-mapper lookups Data Builder's company selection runs: the
  // CSV name→rcid mapping (the same POST /companies the screener AI's
  // company resolver uses), the subsidiary expansion, and the authoritative
  // by-rcid lookup.
  {
    service: 'CompanyMapper',
    viewGroup: 'upstream',
    view: 'company-mapping',
    registryGroup: 'company-mapper',
    impact: 'Data Builder company-mapping uploads + screener AI company lists',
    customUrl: null,
    docsUrl: COMPANY_MAPPER_API_DOCS,
    request: {
      url: '{COMPANY_MAPPER_URL}/companies',
      useAdminAuth: true,
      bodyRaw: 'COMPANY_NAME\nOpenAI',
      headers: { 'Content-Type': 'text/csv;charset=UTF-8' },
    },
  },
  {
    service: 'CompanyMapper',
    viewGroup: 'upstream',
    view: 'company-subsidiaries',
    registryGroup: 'company-mapper',
    impact: 'Data Builder subsidiary selection',
    customUrl: null,
    docsUrl: COMPANY_MAPPER_API_DOCS,
    request: {
      url: '{COMPANY_MAPPER_URL}/subsidiaries',
      useAdminAuth: true,
      body: { rcid: 1116644 },
    },
  },
  {
    service: 'CompanyMapper',
    viewGroup: 'upstream',
    view: 'company-get-company',
    registryGroup: 'company-mapper',
    impact: 'authoritative company-by-rcid lookups (taxonomy tree placement)',
    customUrl: null,
    docsUrl: COMPANY_MAPPER_API_DOCS,
    request: {
      url: '{COMPANY_MAPPER_URL}/get-company',
      useAdminAuth: true,
      body: { rcid: 1116644 },
    },
  },
  // Resume enrichment's parser model, behind go-api. The runner uploads a
  // one-page fixture resume once per process (go-api /file/upload, cached)
  // and re-invokes the parser on that existing upload each sweep — the same
  // two-step flow the resume-enrichment page runs, without per-sweep S3
  // churn. Non-prod environments route to the model's -dev variant, exactly
  // as the page does.
  {
    service: 'ResumeParser',
    viewGroup: 'upstream',
    view: 'resume-parser',
    registryGroup: 'modelling',
    impact: 'resume-enrichment page parsing',
    customUrl: null,
    request: {
      url: '{GO_API_ROOT}/models/resume-parser-processing',
      useAdminAuth: true,
      flow: 'resume-parser',
      timeoutMs: 30_000,
    },
  },
];

// ---------------------------------------------------------------------------
// Shared presentation helpers — the dashboard board and the dash-meta Slack
// alerts must describe a check identically (names, endpoint paths, blast
// radius), so the derivations live here beside the config they read.
// ---------------------------------------------------------------------------

/** Page probes carry a dashboard page's view group; API-only checks use graphql-api/data-api/upstream/shared. */
export const isPageViewGroup = (viewGroup: string): boolean =>
  !['graphql-api', 'data-api', 'upstream', 'shared'].includes(viewGroup);

/**
 * The page path a check's alerts link to: its `customUrl` when it has one
 * (the check's view can predate the route name), else `viewGroup/view`.
 */
export const healthCheckPagePath = (check: HealthCheckConfig): string =>
  (check.customUrl ?? `${check.viewGroup}/${check.view}`).replace(/^\//, '');

/**
 * One-line blast radius for a red check: the hand-written `impact` when set,
 * the page path for page probes, or the pages whose templates run the same
 * public op for API canonicals.
 */
export const describeHealthCheckImpact = (
  check: HealthCheckConfig
): string | null => {
  if (check.impact) return check.impact;
  if (isPageViewGroup(check.viewGroup)) {
    return `${healthCheckPagePath(check)} page`;
  }
  if (check.publicOp) {
    const pages = HEALTH_CHECKS.filter(
      (other) =>
        other.publicOp === check.publicOp && isPageViewGroup(other.viewGroup)
    ).map(healthCheckPagePath);
    if (pages.length) return `pages: ${[...new Set(pages)].join(', ')}`;
    return 'external API consumers';
  }
  return null;
};

/**
 * The checks an environment actually runs and shows. A check tagged
 * `deprecated` is probed in production only — external consumers still live on
 * the op there — while the stage/dev backends don't maintain the legacy paths,
 * so sweeping them off-prod produces nothing but alert noise. Deprecating an op
 * normally retires its probe outright; `selectionList` keeps two, for the
 * public-API surface and for the k17000 lookups behind sentiment reviews. Its
 * date and currency probes are NOT tagged — those are live dashboard
 * dependencies, so they sweep in every environment like any other check.
 */
export const healthChecksForEnvironment = (
  isProduction: boolean
): HealthCheckConfig[] =>
  isProduction
    ? HEALTH_CHECKS
    : HEALTH_CHECKS.filter((check) => !check.deprecated);

/** The URL path a REST check probes, with the `{ENV_VAR}` placeholder or host stripped. */
export const healthCheckRestPath = (
  check: HealthCheckConfig
): string | null => {
  if (!isRestHealthCheck(check)) return null;
  const path = check.request.url
    .replace(/^\{[A-Z0-9_]+\}/, '')
    .replace(/^https?:\/\/[^/]+/, '');
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
};
